import { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../../components/ThemeContext";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

interface WordPressPost {
    id: number;
    title: {
        rendered: string;
    };
    excerpt: {
        rendered: string;
    };
    slug: string;
    link: string;
    _embedded?: {
        'wp:featuredmedia'?: Array<{
            source_url: string;
        }>;
    };
}

// Helper function to truncate text
const truncateExcerpt = (html: string, maxLength: number = 50) => {
    // Remove HTML tags
    const text = html.replace(/<[^>]*>/g, '');
    // Truncate to maxLength and add ellipsis if needed
    return text.length > maxLength 
        ? text.substring(0, maxLength) + '...'
        : text;
};

export const Blog = () => {
    const [theme] = useContext(ThemeContext);
    const [posts, setPosts] = useState<WordPressPost[]>([]);
    const [loading, setLoading] = useState(true);
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const response = await axios.get(
                    `https://veridian.adam-webdev.sk/wp-json/wp/v2/posts?_embed&per_page=100`
                );
                
                const filteredPosts = response.data.filter((post: WordPressPost) => {
                    if (i18n.language === 'sk') {
                        return post.link.includes('/sk/');
                    } else {
                        return !post.link.includes('/sk/');
                    }
                });

                setPosts(filteredPosts);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching posts:', error);
                setLoading(false);
            }
        };

        fetchPosts();
    }, [i18n.language]);

    return (
        <div className={`blog py-4 sm:py-10 ${theme}`}> 
            <div className="container mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-4xl sm:text-6xl font-bold uppercase"> {t('blog.title')}</h1>
                    <p className="max-w-2xl">
                        {t('blog.description')}
                    </p> 
                </div>
                
                {loading ? (
                    <p className="text-center">{t('blog.loading')}</p>
                ) : posts.length > 0 ? (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {posts.map(post => (
                            <article key={post.id} className="border rounded overflow-hidden hover:shadow-lg transition-shadow">
                                {post._embedded?.['wp:featuredmedia']?.[0]?.source_url && (
                                    <img 
                                        src={post._embedded['wp:featuredmedia'][0].source_url}
                                        alt={post.title.rendered}
                                        className="w-full h-48 object-cover"
                                    />
                                )}
                                <div className="p-4">
                                    <h2 
                                        className="text-xl font-semibold mb-2"
                                        dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                                    />
                                    <div 
                                        className="text-sm mb-4"
                                        dangerouslySetInnerHTML={{ 
                                            __html: truncateExcerpt(post.excerpt.rendered, 120) 
                                        }}
                                    />
                                    <Link 
                                        to={`/blog/${post.slug}`}
                                        className="text-black-600"
                                    >
                                        {t('blog.readMore')} →
                                    </Link>
                                </div>
                            </article>
                        ))}
                    </div>
                ) : (
                    <p className="text-center">{t('blog.noPosts')}</p>
                )}
            </div>
        </div>
    );
};



  
  